<template>
	<div class="activity-message-body" :class="{ deletedmsg: isDeleted }">
		<div v-if="parent" class="parent-quote">
			<div class="parent-quote-pill">
				<ActivityText :message="parent" />
			</div>
			<div class="parent-quote-connector" />
		</div>
		<div
			class="message-wrap"
			:class="{
				clamped: !expanded,
				'is-translating': isTranslating,
			}"
			ref="messageWrap"
			@click.capture="onMessageClickCapture"
		>
			<MessagePreview :message="displayedMessage" />
		</div>
		<button
			v-if="overflows"
			type="button"
			class="show-more-btn"
			@click="expanded = !expanded"
		>
			{{
				expanded
					? $t('admin_activity_show_less')
					: $t('admin_activity_show_more')
			}}
		</button>
	</div>
</template>

<script>
import MessagePreview from '@/components/Message/MessagePreview'
import ActivityText from './ActivityText'
import store, { normalizeMessage } from '@/store'
import { post } from '@/helpers/api'

export default {
	name: 'ActivityMessageBody',

	components: { MessagePreview, ActivityText },

	props: {
		item: { type: Object, required: true },
	},

	data() {
		return {
			expanded: false,
			overflows: false,
			translated: false,
			translatedTrans: null,
			isTranslating: false,
		}
	},

	computed: {
		message() {
			return this.item.message
		},

		parent() {
			const p = this.item.parent
			if (!p?.text) return null
			return p
		},

		isDeleted() {
			return !!this.message?.deleted_at
		},

		// Message we hand to MessagePreview. Translation display is driven by
		// our own translatedTrans/translated state (toggled via the intercepted
		// globe click), not the store. Note: MessageText still runs its
		// mount-time translation check and may call updateMessage(isTranslated);
		// for the activity feed's skip-store messages that write lands on a
		// throwaway messagesById[undefined] entry rather than the real message.
		// Three rendering modes:
		// - translating: substitute the body text with the localized
		//   "Translating…" label so the card doesn't grow/shrink
		// - translated: overlay messagetrans + isTranslated so MessageText
		//   renders the translated text
		// - default: original text
		displayedMessage() {
			if (this.isTranslating) {
				const placeholder = this.$t('chat_translation_inprogress')
				return {
					...this.message,
					message: placeholder,
					text: placeholder,
					messagetrans: null,
					isTranslated: false,
				}
			}
			if (this.translated && this.translatedTrans) {
				return {
					...this.message,
					messagetrans: this.translatedTrans,
					isTranslated: true,
				}
			}
			return { ...this.message, isTranslated: false }
		},
	},

	mounted() {
		this.measureOverflow()
		this.$onEventBus({
			'message-change': (ids) => {
				if (ids?.includes(this.message?.id))
					this.$nextTick(this.measureOverflow)
			},
		})
	},

	updated() {
		if (!this.expanded) this.measureOverflow()
	},

	methods: {
		measureOverflow() {
			const el = this.$refs.messageWrap?.querySelector('.message-content')
			if (!el) {
				this.overflows = false
				return
			}
			this.overflows = el.scrollHeight - el.clientHeight > 1
		},

		// Intercept clicks on MessageText's built-in globe before its own
		// onGlobeClick fires, so we can run our own translation toggle without
		// touching the message store or localStorage.
		onMessageClickCapture(e) {
			if (!e.target?.closest?.('.globe')) return
			e.stopImmediatePropagation()
			e.preventDefault()
			this.toggleTranslation()
		},

		async toggleTranslation() {
			if (this.isTranslating) return
			if (this.translated) {
				this.translated = false
				return
			}
			if (!this.translatedTrans) {
				this.isTranslating = true
				try {
					const { data } = await post('/api/v1/message.translate', {
						message_id: this.message.id,
						lang: store.auth?.user?.language || 'en',
					})
					// Run the response through normalizeMessage (skipping the store)
					// so messagetrans lands in the shape MessageText expects:
					// { lang, translation, ... }
					const normalized = normalizeMessage(data?.message, {
						skipStore: true,
					})
					this.translatedTrans = normalized?.messagetrans || null
				} catch (e) {
					console.error(e)
				} finally {
					this.isTranslating = false
				}
			}
			if (this.translatedTrans) this.translated = true
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.activity-message-body {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.message-wrap {
	&.clamped :deep(.message-content) {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 4;
		overflow: hidden;
	}
}

.message-wrap.is-translating :deep(.message-text) {
	font-style: italic;
	color: var(--neutral-500);
}

.parent-quote {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 4px;
	margin-bottom: -8px;
}

.parent-quote + .message-wrap :deep(.message-preview) {
	padding-top: 0;
}

.parent-quote-pill {
	background: var(--neutral-100);
	color: var(--neutral-600);
	font-family: Avenir-Roman;
	font-size: 14px;
	line-height: 19px;
	padding: 4px 10px;
	border-radius: 14px;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.parent-quote-connector {
	width: 2px;
	height: 14px;
	background: var(--teal-300);
	border-radius: 1px;
	margin-left: 32px;
}

.deletedmsg :deep(.message-text) {
	color: var(--neutral-500);
	text-decoration: line-through;
}

.show-more-btn {
	align-self: flex-start;
	margin-left: 62px;
	background: none;
	border: none;
	padding: 0;
	font-family: Avenir-Medium;
	font-size: 14px;
	line-height: 20px;
	color: var(--blue-400);
	cursor: pointer;
}
</style>
